import { createAsyncThunk } from '@reduxjs/toolkit';
import defaultRateToolsService from '../../common/api/defaultRateToolsService';
import {
  AdditionalServiceRuleItem,
  PriceChangeItem,
  WeekdayCheckInAndCheckOutTimeItem,
  WeekdayCheckInRuleItem,
  WeekdayMinimumStayRuleItem,
  WeekdayPriceChangeRuleItem,
} from '../../common/types/rates.types';
import { RatePriceItem } from '../defaultRates/defaultRatesSlice';

export interface DeleteRulesFromSeasonsRequest {
  accommodationId: string;
  selectedSeasonRateIds: string[];
  ruleType?: string;
}

export interface CopyPreparationRestrictionRuleToSeasonsRequest {
  accommodationId?: string;
  copyStartDate?: string;
  copyEndDate?: string;
  selectedSeasonRateIds: string[];
  preparationDays: number;
}

export interface CopyMinimumLengthOfStayRuleToSeasonsRequest {
  accommodationId?: string;
  copyStartDate?: string;
  copyEndDate?: string;
  selectedSeasonRateIds: string[];
  minimumLengthOfStay: number;
}

export interface CopyShortStayRuleToSeasonsRequest {
  accommodationId?: string;
  copyStartDate?: string;
  copyEndDate?: string;
  selectedSeasonRateIds: string[];
  durationRestriction: number;
  priceChange: PriceChangeItem;
}

export interface CopyLastMinuteRuleToSeasonsRequest {
  accommodationId?: string;
  copyStartDate?: string;
  copyEndDate?: string;
  selectedSeasonRateIds: string[];
  daysUntilStart: number;
  priceChange: PriceChangeItem;
}

export interface CopyPriceChangeRuleToSeasonsRequest {
  accommodationId?: string;
  selectedSeasonRateIds: string[];
  priceChange: PriceChangeItem;
}

export interface CopyReleaseIntervalRuleToSeasonsRequest {
  accommodationId?: string;
  selectedSeasonRateIds: string[];
  daysUntilRelease: number;
  releaseLengthOfStayRule: boolean;
  releaseWeekdayMinimumStayRules: boolean;
  releaseWeekdayCheckInRules: boolean;
  releaseWeekdayCheckOutRules: boolean;
}

export interface CopyWeekdayCheckInAndCheckOutRulesToSeasonsRequest {
  accommodationId?: string;
  copyStartDate?: string;
  copyEndDate?: string;
  selectedSeasonRateIds: string[];
  blockedCheckInWeekdays: WeekdayCheckInRuleItem[];
  blockedCheckOutWeekdays: WeekdayCheckInRuleItem[];
}

export interface CopyWeekdayMinimumStayRulesToSeasonsRequest {
  accommodationId?: string;
  copyStartDate?: string;
  copyEndDate?: string;
  selectedSeasonRateIds: string[];
  weekdayMinimumDurations: WeekdayMinimumStayRuleItem[];
}

export interface CopyWeekdayPriceChangeRulesToSeasonsRequest {
  accommodationId?: string;
  copyStartDate?: string;
  copyEndDate?: string;
  selectedSeasonRateIds: string[];
  weekdayPriceChanges: WeekdayPriceChangeRuleItem[];
}

export interface CopyWeekdayCheckInCheckOutTimeRulesToSeasonsRequest {
  accommodationId?: string;
  copyStartDate?: string;
  copyEndDate?: string;
  selectedSeasonRateIds: string[];
  checkInTimes: WeekdayCheckInAndCheckOutTimeItem[];
  checkOutTimes: WeekdayCheckInAndCheckOutTimeItem[];
}

export interface CopyCheckInCheckOutTimesToSeasonsRequest {
  accommodationId?: string;
  copyStartDate?: string;
  copyEndDate?: string;
  selectedSeasonRateIds: string[];
  checkInTime: string;
  checkOutTime: string;
}

export interface CopyAdditionalServiceRulesToSeasonsRequest {
  accommodationId?: string;
  selectedSeasonRateIds: string[];
  additionalServiceRules: AdditionalServiceRuleItem[];
}

export interface CopyPricesToSeasonsRequest {
  accommodationId?: string;
  copyStartDate?: string;
  copyEndDate?: string;
  selectedSeasonRateIds: string[];
  priceForAdditionalDay: number;
  commissionPercent?: number;
  ratePrices: RatePriceItem[];
}

export const copyPreparationRestrictionRuleToSeasons = createAsyncThunk(
  'defaultRateTools/copyPreparationRestrictionRuleToSeasons',
  async (
    request: CopyPreparationRestrictionRuleToSeasonsRequest,
    { rejectWithValue },
  ) => {
    try {
      return await defaultRateToolsService.copyPreparationRestrictionRuleToSeasons(
        request,
      );
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const copyMinimumLengthOfStayRuleToSeasons = createAsyncThunk(
  'defaultRateTools/copyMinimumLengthOfStayRuleToSeasons',
  async (
    request: CopyMinimumLengthOfStayRuleToSeasonsRequest,
    { rejectWithValue },
  ) => {
    try {
      return await defaultRateToolsService.copyMinimumLengthOfStayRuleToSeasons(
        request,
      );
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const copyShortStayRuleToSeasons = createAsyncThunk(
  'defaultRateTools/copyShortStayRuleToSeasons',
  async (request: CopyShortStayRuleToSeasonsRequest, { rejectWithValue }) => {
    try {
      return await defaultRateToolsService.copyShortStayRuleToSeasons(request);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const copyLastMinuteRuleToSeasons = createAsyncThunk(
  'defaultRateTools/copyLastMinuteRuleToSeasons',
  async (request: CopyLastMinuteRuleToSeasonsRequest, { rejectWithValue }) => {
    try {
      return await defaultRateToolsService.copyLastMinuteRuleToSeasons(request);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const copyPriceChangeRuleToSeasons = createAsyncThunk(
  'defaultRateTools/copyPriceChangeRuleToSeasons',
  async (request: CopyPriceChangeRuleToSeasonsRequest, { rejectWithValue }) => {
    try {
      return await defaultRateToolsService.copyPriceChangeRuleToSeasons(
        request,
      );
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const copyRateRuleReleaseIntervalToSeasons = createAsyncThunk(
  'defaultRateTools/copyReleaseIntervalRuleToSeasons',
  async (
    request: CopyReleaseIntervalRuleToSeasonsRequest,
    { rejectWithValue },
  ) => {
    try {
      return await defaultRateToolsService.copyReleaseIntervalRuleToSeasons(
        request,
      );
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const copyWeekdayCheckInAndCheckOutRulesToSeasons = createAsyncThunk(
  'defaultRateTools/copyWeekdayCheckInAndCheckOutRulesToSeasons',
  async (
    request: CopyWeekdayCheckInAndCheckOutRulesToSeasonsRequest,
    { rejectWithValue },
  ) => {
    try {
      return await defaultRateToolsService.copyWeekdayCheckInAndCheckOutRules(
        request,
      );
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const copyWeekdayMinimumStayRulesToSeasons = createAsyncThunk(
  'defaultRateTools/copyWeekdayMinimumStayRulesToSeasons',
  async (
    request: CopyWeekdayMinimumStayRulesToSeasonsRequest,
    { rejectWithValue },
  ) => {
    try {
      return await defaultRateToolsService.copyWeekdayMinimumStayRules(request);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const copyWeekdayPriceChangeRulesToSeasons = createAsyncThunk(
  'defaultRateTools/copyWeekdayPriceChangeRulesToSeasons',
  async (
    request: CopyWeekdayPriceChangeRulesToSeasonsRequest,
    { rejectWithValue },
  ) => {
    try {
      return await defaultRateToolsService.copyWeekdayPriceChangeRules(request);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const copyWeekdayCheckInCheckOutTimeRulesToSeasons = createAsyncThunk(
  'defaultRateTools/copyWeekdayCheckInCheckOutTimeRulesToSeasons',
  async (
    request: CopyWeekdayCheckInCheckOutTimeRulesToSeasonsRequest,
    { rejectWithValue },
  ) => {
    try {
      return await defaultRateToolsService.copyWeekdayCheckInCheckOutTimeRules(
        request,
      );
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const copyCheckInCheckOutTimesToSeasons = createAsyncThunk(
  'defaultRateTools/copyCheckInCheckOutTimesToSeasons',
  async (
    request: CopyCheckInCheckOutTimesToSeasonsRequest,
    { rejectWithValue },
  ) => {
    try {
      return await defaultRateToolsService.copyCheckInCheckOutTimes(request);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const copyAdditionalServiceRulesToSeasons = createAsyncThunk(
  'defaultRateTools/copyAdditionalServiceRulesToSeasons',
  async (
    request: CopyAdditionalServiceRulesToSeasonsRequest,
    { rejectWithValue },
  ) => {
    try {
      return await defaultRateToolsService.copyAdditionalServiceRules(request);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const copyPricesToSeasons = createAsyncThunk(
  'defaultRateTools/copyPricesToSeasons',
  async (request: CopyPricesToSeasonsRequest, { rejectWithValue }) => {
    try {
      return await defaultRateToolsService.copyPrices(request);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const deleteRulesFromSeasonsAsync = createAsyncThunk(
  'defaultRateTools/deleteRulesFromSeasons',
  async (request: DeleteRulesFromSeasonsRequest, { rejectWithValue }) => {
    try {
      return await defaultRateToolsService.deleteRulesFromSeasons(request);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);
